/**
 * The venue's images on the wall, and the timer that decides when one of them
 * is worth interrupting for.
 *
 * Three kinds come back from /ads and they are not interchangeable:
 *
 *   fullscreen  16:9, the whole screen. Shown before the draw and after the
 *               night ends — the two windows where there is nothing else to
 *               put up.
 *   sidebar     9:16 portrait. THE INTERRUPT. During play it takes the side
 *               panel for half a minute every ten minutes and then gives it
 *               back. It does not hold a column open all night: the boards and
 *               the standings are what the room is looking at, and an advert
 *               does not need permanent screen space to be seen by everyone in
 *               a pub over the course of an evening.
 *   fallback    one per venue, the stand-in when a venue has uploaded nothing
 *               of the kind a slot wants.
 *
 * NOTHING HERE EVER RENDERS A BROKEN IMAGE. A venue with no ads, an ads call
 * that fails and an R2 object that has gone missing all end at the same text
 * screen, because the failure mode of this component is a pub wall.
 */

import { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../../lib/api.js';

/** A fullscreen slide. Not venue-configurable: nothing is behind it to lose. */
export const FULLSCREEN_MS = 15_000;

/** A sidebar slide, when more than one is uploaded and the interrupt is long. */
const SIDEBAR_MS = 10_000;

/**
 * The sidebar interrupt's timing, from the venue's own settings.
 *
 *   tv_sidebar_interval_seconds  darts between interrupts
 *   tv_sidebar_display_seconds   how long the ad holds the panel
 *   tv_sidebar_warmup_seconds    the wait before the FIRST one
 *
 * A venue that has set none of them gets ten minutes, thirty seconds, and a
 * first interrupt one interval in — which is the only default that cannot
 * surprise somebody who has never touched these: an ad appearing eight seconds
 * after a screen is switched on looks like the screen is broken.
 *
 * EVERY VALUE IS CLAMPED, because these come out of a key-value table anybody
 * with owner rights can PUT a number into, and the failure modes are on a wall
 * in front of customers: a two-second interval is a strobe, and a display time
 * longer than the interval is an ad that never leaves. Values arrive as TEXT
 * and a blank or unparseable one falls back rather than becoming NaN.
 */
export const SIDEBAR_DEFAULTS = {
  intervalSeconds: 600,
  displaySeconds:  30,
};

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

function seconds(raw, fallback) {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function sidebarTiming(settings) {
  const interval = clamp(
    seconds(settings?.tv_sidebar_interval_seconds, SIDEBAR_DEFAULTS.intervalSeconds),
    30, 3 * 60 * 60
  );

  // Capped at the interval, so the standings always hold the panel for at
  // least as long as the ad that interrupted them did.
  const display = clamp(
    seconds(settings?.tv_sidebar_display_seconds, SIDEBAR_DEFAULTS.displaySeconds),
    5, Math.min(300, interval)
  );

  // Unset warms up for a full interval rather than zero: an interrupt is an
  // interruption, and there is nothing to interrupt in the first seconds after
  // a screen comes up. Zero is still allowed — a venue that types it means it.
  const warmup = clamp(
    seconds(settings?.tv_sidebar_warmup_seconds, interval),
    0, 3 * 60 * 60
  );

  return {
    everyMs:  interval * 1000,
    forMs:    display  * 1000,
    warmupMs: warmup   * 1000,
  };
}

/* ------------------------------------------------------------------------- */

/**
 * A venue's ads, split by kind, fetched once.
 *
 * Once and not on a timer: this screen runs for an evening and an ad uploaded
 * mid-night is not worth a poll on every TV in the venue. Reloading the page
 * picks up a change, which is what an admin who just uploaded one will do.
 *
 * The route takes venue_admin, which this screen has — it sits behind
 * ProtectedRoute venueAdmin. The images themselves are public; only the list is
 * not.
 */
export function useVenueAds(venueId) {
  const [state, setState] = useState({ status: 'loading', ads: [] });

  useEffect(() => {
    if (venueId == null) return undefined;

    let cancelled = false;
    setState({ status: 'loading', ads: [] });

    (async () => {
      const { data, error } = await apiGet(`/api/venues/${venueId}/ads`);
      if (cancelled) return;

      // An ads call that fails is not an error the room can act on. The screen
      // falls through to its text state and the night carries on.
      setState(error
        ? { status: 'error', ads: [] }
        : { status: 'ready', ads: data?.ads ?? [] });
    })();

    return () => { cancelled = true; };
  }, [venueId]);

  return useMemo(() => ({
    status:     state.status,
    sidebar:    state.ads.filter((a) => a.kind === 'sidebar'),
    fullscreen: state.ads.filter((a) => a.kind === 'fullscreen'),
    fallback:   state.ads.find((a) => a.kind === 'fallback') ?? null,
  }), [state]);
}

/**
 * Step through a list on a timer, preloading the next one.
 *
 * The preload is the point of doing this here rather than with CSS: a slide
 * that starts loading when it is shown is a white rectangle on the wall for as
 * long as the venue's wifi takes, and these are photographs.
 */
export function useRotation(items, intervalMs, enabled = true) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  // A venue uploading one mid-rotation must not leave the index past the end.
  useEffect(() => { setIndex(0); }, [count]);

  useEffect(() => {
    if (!enabled || count < 2) return undefined;

    const id = setInterval(() => setIndex((i) => (i + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [enabled, count, intervalMs]);

  useEffect(() => {
    if (count < 2) return;
    const next = items[(index + 1) % count];
    if (next?.url) {
      const img = new Image();
      img.src = next.url;
    }
  }, [items, index, count]);

  return count ? items[index % count] ?? null : null;
}

/**
 * Whether the sidebar ad is on screen right now.
 *
 * Two alternating timeouts rather than one interval, because the two phases
 * have different lengths and an interval would have to carry a phase counter
 * to tell them apart. It starts in the waiting phase on purpose: a TV switched
 * on during a leg shows the darts first.
 *
 * The warm-up is separate from the interval because the first wait is a
 * different question from the rest. A venue that wants its first ad soon after
 * the boards go up and ten minutes between them afterwards can say so, and one
 * that sets neither gets an interval-long wait for both.
 */
export function useAdInterrupt({ enabled, everyMs, forMs, warmupMs }) {
  const [showing, setShowing] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setShowing(false);
      return undefined;
    }

    let timer;

    const show = () => {
      setShowing(true);
      timer = setTimeout(rest, forMs);
    };

    const rest = () => {
      setShowing(false);
      timer = setTimeout(show, everyMs);
    };

    timer = setTimeout(show, warmupMs ?? everyMs);

    return () => {
      clearTimeout(timer);
      setShowing(false);
    };
  }, [enabled, everyMs, forMs, warmupMs]);

  return showing;
}

/**
 * The TV's display toggles, from the same public settings the timings use.
 *
 * Values arrive as TEXT ('1' / '0') out of a key-value table, and UNSET IS NOT
 * FALSE — each key has its own default, and two of the three differ from the
 * third:
 *
 *   liveAverages  OFF. Averages stay hidden mid-match for the reason TvBoards
 *                 gives: they swing wildly on a single visit. A venue that
 *                 wants them anyway can say so, and nothing changes for a venue
 *                 that has never opened the settings page.
 *   followQr      ON. It is the only one that is useful on day one without
 *                 anybody configuring anything — a code on the wall that puts
 *                 the live view on a spectator's own phone. A feature nobody
 *                 knows to turn on is a feature nobody uses.
 *   totalRaised   OFF. It gates the Start/Stop control on the admin dashboard,
 *                 and a venue not running a charity has no use for a button
 *                 that puts a fundraising total over the darts.
 *
 * Anything other than '1' or '0' — a hand-edited row, a stray space — reads as
 * the default rather than as false. These decide what a pub wall does.
 */
export const TOGGLE_DEFAULTS = {
  liveAverages: false,
  followQr:     true,
  totalRaised:  false,
};

function flag(raw, fallback) {
  if (raw === '1') return true;
  if (raw === '0') return false;
  return fallback;
}

export function tvToggles(settings) {
  return {
    liveAverages: flag(settings?.tv_live_averages,        TOGGLE_DEFAULTS.liveAverages),
    followQr:     flag(settings?.tv_follow_qr,            TOGGLE_DEFAULTS.followQr),
    totalRaised:  flag(settings?.tv_total_raised_enabled, TOGGLE_DEFAULTS.totalRaised),
  };
}

/* ------------------------------------------------------------------------- */

/**
 * An uploaded image, with the one thing a wall display must never do handled.
 *
 * A key that 404s — an object deleted out from under a row, an R2 blip — is
 * swapped for the text screen rather than left as a browser's broken-image
 * glyph two metres wide.
 */
function AdImage({ ad, className, children }) {
  const [broken, setBroken] = useState(false);

  // A new ad in the same slot deserves its own attempt.
  useEffect(() => { setBroken(false); }, [ad?.url]);

  if (!ad?.url || broken) return children ?? null;

  return (
    <img
      className={className}
      src={ad.url}
      alt=""
      onError={() => setBroken(true)}
    />
  );
}

/**
 * One uploaded image in the shape the wall will give it — for the admin page.
 *
 * IT SHARES AdImage AND .hd-tv-ad-image WITH THE WALL, which is the whole
 * reason it lives in this file rather than in the settings page. The migration
 * comment for venue_ads promises that "the admin preview uses the same crop
 * logic, so the preview cannot drift from what the TV draws"; the only way to
 * keep that promise is for both to BE the same component, rather than two that
 * happen to agree today.
 *
 * NOTE THAT THERE IS NO CROP, and the promise survives that. The wall uses
 * `contain`, deliberately — see the Ads block in global.css: "these are a local
 * business's artwork and cropping a phone number off the bottom of one is worse
 * than the letterboxing." So the preview letterboxes too, and an admin sees the
 * bars they are actually going to get rather than a tidier lie.
 *
 * The frame's aspect ratio is the SLOT's, not the image's: 9:16 for the sidebar
 * interrupt, 16:9 for a fullscreen slide. That is what makes the preview worth
 * looking at — it answers "how much of that screen will my picture fill", which
 * is the question somebody uploading a photo off their phone actually has.
 */
export function AdSlotPreview({ ad, kind, children }) {
  return (
    <div className={`hd-ad-preview hd-ad-preview-${kind}`}>
      <AdImage ad={ad} className="hd-tv-ad-image">
        {children ?? <span className="hd-ad-preview-empty">No image</span>}
      </AdImage>
    </div>
  );
}

/**
 * The screen a venue with no images gets. Its own name, large, and whatever
 * the page knows about what happens next.
 *
 * `welcome` is off by default and ON for the fullscreen backdrop only. That
 * screen is the one a room looks at before the darts start, and it is meant to
 * greet them — the other two uses of this component are a 9:16 sidebar slot and
 * a "Waiting for the boards" panel mid-night, neither of which is welcoming
 * anybody who has not already been welcomed.
 *
 * A venue whose name has not loaded yet falls back to 'Darts' and gets NO
 * prefix: "Welcome to Darts" reads like a broken template, which is exactly
 * what it would be.
 */
export function TvTextScreen({ venueName, note, welcome = false }) {
  return (
    <div className="hd-tv-textscreen">
      <div className="hd-tv-textscreen-name">
        {venueName ? (welcome ? `Welcome to ${venueName}` : venueName) : 'Darts'}
      </div>
      {note && <div className="hd-tv-textscreen-note">{note}</div>}
    </div>
  );
}

/** The whole screen: before the draw, and after the night is over. */
export function TvFullscreenAds({ ads, venueName, note }) {
  const current = useRotation(ads.fullscreen, FULLSCREEN_MS);
  const slide   = current ?? ads.fallback;

  return (
    <div className="hd-tv-fullscreen-ad">
      <AdImage ad={slide} className="hd-tv-ad-image">
        <TvTextScreen venueName={venueName} note={note} welcome />
      </AdImage>
    </div>
  );
}

/**
 * The interrupt itself, in the side panel.
 *
 * Rotates only if the venue has several and the interrupt is long enough to
 * show more than one; otherwise a single image sits there for its half minute.
 */
export function TvSidebarAd({ ads, venueName, forMs }) {
  const rotate  = ads.sidebar.length > 1 && forMs >= SIDEBAR_MS * 2;
  const current = useRotation(ads.sidebar, SIDEBAR_MS, rotate);
  const slide   = current ?? ads.sidebar[0] ?? ads.fallback;

  return (
    <div className="hd-tv-sidebar-ad">
      <AdImage ad={slide} className="hd-tv-ad-image">
        <TvTextScreen venueName={venueName} />
      </AdImage>
    </div>
  );
}
